'use client';

import {useMemo} from 'react';
import {publishEvent} from '@udemy-v2/event-tracking';
import {BuyNowEvent, EnrollNowEvent, LearningProduct} from '../events/events';
import {useClpCourse} from '../context/clp-course-provider.react-component';
import {useClcBuyButtonContext} from '@/lib/hooks/use-clc-buy-button-context';

/**
 * Builds the CLP primary CTA state for authenticated organization members.
 *
 * Text, URL, style, and event type are sourced directly from the monolith's
 * `buy_button.button` payload (get_buy_button_context in
 * udemy/course_landing_page/helpers.py) so business logic stays on the
 * backend. The hook is a thin passthrough plus event wiring.
 */
export function useOrgPrimaryCtaButton() {
    const course = useClpCourse();
    const org = useClcBuyButtonContext();

    const eventPayload = useMemo(
        () => ({
            buyable: {
                id: Number(course.id),
                type: LearningProduct.COURSE,
                trackingId: course.frontendTrackingId || course.id,
            },
        }),
        [course.id, course.frontendTrackingId],
    );

    return useMemo(() => {
        if (!org) {
            return {
                checkoutUrl: '',
                trackEvent: () => {},
                isLoading: true as const,
            };
        }

        const trackEvent = () => {
            switch (org.event_type) {
                case 'enroll_now':
                    publishEvent(new EnrollNowEvent(eventPayload));
                    return;
                case 'buy_now':
                    publishEvent(new BuyNowEvent(eventPayload));
                    return;
                default:
                    return;
            }
        };

        return {
            text: org.text,
            checkoutUrl: org.buy_url ?? '',
            trackEvent,
            isLoading: false as const,
            udStyle: org.style,
        };
    }, [org, eventPayload]);
}
